import { queryOptions } from '@tanstack/react-query'
import { ApiError, apiExchange, apiGet, apiPost, apiPostForm, type PostValue } from './client'
import { saveBytes } from './inventoree'

/**
 * Autoevaluarea DNSC a firmei (fila "DNSC self-assessment" din /overview/nis2):
 * catalogul CyberFundamentals 2025, raspunsurile cu istoric si scorul ca in
 * foile EVAL_MMS. Capetele: `OverviewDnscAssessmentController`.
 *
 * Nota de implementare PROPUSA din date nu vine de aici, ci din
 * `/overview/nis2/dnsc` (`nis2DnscQuery`) - aceeasi pe care o arata sectiunea
 * de sub litere, deci un singur raspuns la aceeasi intrebare.
 */

export type AssuranceLevel = 'B' | 'I' | 'E'

export interface AssessmentAnswer {
  doc_score: number | null
  impl_score: number | null
  not_applicable: boolean
  na_reason: string | null
  doc_proposed: number | null
  impl_proposed: number | null
  impl_choice: 'accepted' | 'override' | 'manual' | null
  override_reason: string | null
  responsible: string | null
  review_date: string | null
  note: string | null
  evidence_name: string | null
  evidence_url: string | null
  /** Documentul incarcat (serverul de fisiere), langa nume + link; lipsesc pe raspunsurile vechi. */
  evidence_file_id?: number | null
  evidence_file?: EvidenceFile | null
  answered_by_email: string | null
  answered_at: string
  versions: number
}

export interface EvidenceFile {
  id: number
  name: string
  size: number
  uploaded_at?: string
}

export interface DocumentationProposal {
  score: number
  why: string
  source: string
}

export interface AssessmentRequirement {
  code: string
  function: string
  function_name: string
  category: string
  category_name: string
  subcategory: string
  subcategory_text: string
  /** Nivelul la care intra cerinta (B e inclus in I, I in E). */
  level: AssuranceLevel
  /** Literele nivelurilor pe care e masura cheie / control de management / e voie N/A. */
  key_levels: string
  management_levels: string
  na_levels: string
  /** Textul DNSC, in romana. */
  text: string
  /** Intrebarile in limbaj simplu - inca goale (pasul urmator). */
  questions: { documentation: string | null; implementation: string | null }
  documentation_proposal: DocumentationProposal | null
  answer: AssessmentAnswer | null
}

export interface LevelRules {
  na_score: number
  na_quota: number
  management_never_na: boolean
  target_total: number
  target_key: number
  target_category: number | null
  file: string
}

export interface MaturityStage {
  score: number
  name: string
  documentation: string
  implementation: string
}

export interface ScoredCategory {
  name: string
  function: string
  documentation: number
  implementation: number
  score: number
  target: number | null
  met: boolean | null
}

export interface AssessmentScore {
  level: AssuranceLevel
  asked: number
  answered: number
  partial: number
  not_applicable: { used: number; quota: number; over: boolean; beyond: string[]; score: number }
  subcategories: Record<string, { documentation: number; implementation: number; category: string }>
  categories: Record<string, ScoredCategory>
  functions: Record<string, { name: string; score: number; categories: number }>
  key_measures: Record<string, { documentation: number | null; implementation: number | null; score: number | null; target: number; met: boolean }>
  total: { score: number | null; target: number; met: boolean }
  targets: { met: boolean; key_measures_below: string[]; categories_below: string[] }
}

/** O constatare IT Audit a planului de masuri (`DnscFindings::rows`). */
export interface PlanRow {
  code: string
  title: string
  severity: 'High' | 'Medium' | 'Low'
  state: string
  owner: string
  due_on: string
  overdue: boolean
  fingerprint: string
  created_at: string
}

export interface AssessmentPage {
  ready: boolean
  reason?: string
  framework?: { code: string; version: string; title: string; cyfun: string | null }
  company?: number
  canAnswer?: boolean
  cannotAnswer?: string
  level?: { level: AssuranceLevel; note: string | null; set_by_email: string | null; set_at: string } | null
  levels?: Partial<Record<AssuranceLevel, LevelRules>>
  stages?: MaturityStage[]
  requirements?: AssessmentRequirement[]
  score?: AssessmentScore | null
  /** Dreptul autoevaluarii; `isAdmin` = cine vede nota despre cui sa-l dea. */
  rights?: { isAdmin: boolean; read: string; edit: string }
  /** Incarcarea de documente: numai cu serverul de fisiere configurat. */
  uploads?: { enabled: boolean; max_mb: number; types: string[] }
  /** Planul de masuri, scris ca constatari IT Audit; `ready` = tabela constatarilor exista. */
  plan?: { ready: boolean; rows: PlanRow[] }
}

export interface SaveResult {
  ok: boolean
  error?: string
  unchanged?: boolean
  answer?: AssessmentAnswer
}

/** GET /api/v2/overview/nis2/assessment */
export const assessmentQuery = queryOptions({
  queryKey: ['overview', 'nis2', 'assessment'],
  queryFn: () => apiGet<AssessmentPage>('/overview/nis2/assessment'),
})

/** GET /api/v2/overview/nis2/assessment/history/{code} - cel mai nou primul. */
export function assessmentHistoryQuery(code: string) {
  return queryOptions({
    queryKey: ['overview', 'nis2', 'assessment', 'history', code],
    queryFn: () => apiGet<{ ok: boolean; error?: string; history?: AssessmentAnswer[] }>(`/overview/nis2/assessment/history/${encodeURIComponent(code)}`),
  })
}

export function saveAssessmentLevel(level: AssuranceLevel, note: string): Promise<SaveResult> {
  return apiPost<SaveResult>('/overview/nis2/assessment/level', { level, note })
}

export function saveAssessmentAnswer(fields: Record<string, PostValue>): Promise<SaveResult> {
  return apiPost<SaveResult>('/overview/nis2/assessment/answer', fields)
}

export interface PlanResult {
  ok: boolean
  error?: string
  opened?: number
  updated?: number
  reopened?: number
  closed?: number
  plan?: PlanRow[]
}

/** POST /api/v2/overview/nis2/assessment/plan - "Generate plan of measures", idempotent. */
export function generatePlan(): Promise<PlanResult> {
  return apiPost<PlanResult>('/overview/nis2/assessment/plan', {})
}

/**
 * GET .../export.xlsx | .../export.pdf. Cand nu se poate (fara nivel, fara
 * firma), serverul raspunde JSON `{ok: false, error}`, si aia se arata - nu se
 * salveaza un fisier care nu e cel cerut.
 */
export async function downloadAssessment(kind: 'xlsx' | 'pdf'): Promise<void> {
  const out = await apiExchange(`/overview/nis2/assessment/export.${kind}`, { method: 'GET' })
  const wanted = kind === 'pdf' ? 'pdf' : 'spreadsheetml'
  if (!out.type.includes(wanted)) {
    let message = 'The file could not be produced.'
    try {
      const body = JSON.parse(out.text) as { error?: string; message?: string }
      message = body.error ?? body.message ?? message
    } catch {
      /* Nu era JSON; ramane propozitia de mai sus. */
    }
    throw new ApiError(out.status || 500, message)
  }
  saveBytes(out.bytes, out.type, out.filename, kind === 'pdf' ? 'DNSC-autoevaluare.pdf' : 'DNSC-EVAL_MMS.xlsx')
}

export interface UploadResult {
  ok: boolean
  error?: string
  file?: EvidenceFile
}

/**
 * POST /api/v2/overview/nis2/assessment/evidence (multipart: code, file).
 * Numai incarca; raspunsul il leaga la "Save" (`evidence_file_id`).
 */
export function uploadEvidence(code: string, file: File): Promise<UploadResult> {
  const body = new FormData()
  body.append('code', code)
  body.append('file', file)
  return apiPostForm<UploadResult>('/overview/nis2/assessment/evidence', body)
}

/** GET /api/v2/overview/nis2/assessment/evidence/{id} - documentul, ca fisier. */
export async function downloadEvidence(file: EvidenceFile): Promise<void> {
  const out = await apiExchange(`/overview/nis2/assessment/evidence/${file.id}`, { method: 'GET' })
  if (out.status !== 200) {
    let message = 'The document could not be read.'
    try {
      message = (JSON.parse(out.text) as { message?: string }).message ?? message
    } catch {
      /* Nu era JSON. */
    }
    throw new ApiError(out.status || 500, message)
  }
  saveBytes(out.bytes, out.type, out.filename, file.name)
}
